import { useMemo, useState } from 'react';
import { Search as Lupe, Clock, Download as DlIcon, Star } from 'lucide-react';
import { api } from '../lib/api';
import { bytes, relativ } from '../lib/format';
import { JobDialog } from '../components/JobDialog';

type Release = {
  guid: string;
  title: string;
  size: number | null;
  grabs: number;
  posted_at: string | null;
  season: number | null;
  episode: number | null;
  quality_score: number;
  recommended: boolean;
  complete_season: boolean;
  tags: { resolution: string; source: string; codec: string; hdr: string; group: string; languages: string[] };
};
type Season = { season: number | null; episode_count: number; release_count: number; releases: Release[] };
type Gruppe = {
  key: string; title: string; year: number | null; type: string;
  cover: string | null; rating: number | null; genres: string[] | null; plot: string | null;
  resolutions: string[]; seasons: number[]; languages: string[];
  releases: Release[]; seasons_detail?: Season[]; total_grabs: number;
};

const AUFLOESUNGEN = ['2160p', '1080p', '720p', 'SD'];

/**
 * Suche.
 *
 * Die alte Seite hatte ein Suchfeld, eine Kategorie und "Max Age" ohne
 * Einheit — keine Filter fuer Aufloesung, Groesse oder Sprache, obwohl die
 * Cron-Jobs genau danach filtern. Und es gab keinen Weg von einem Treffer
 * zu einem Auftrag; man musste den Titel abtippen.
 */
export function Search() {
  const [q, setQ] = useState('');
  const [maxage, setMaxage] = useState('');
  const [gruppen, setGruppen] = useState<Gruppe[] | null>(null);
  const [gesamt, setGesamt] = useState<{ gruppen: number; releases: number } | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [res, setRes] = useState<string[]>([]);
  const [minGB, setMinGB] = useState('');
  const [jobAus, setJobAus] = useState<Gruppe | null>(null);

  async function suchen(e?: React.FormEvent) {
    e?.preventDefault();
    if (!q.trim()) return;
    setLaedt(true);
    setFehler(null);
    try {
      const p = new URLSearchParams({ q: q.trim() });
      if (maxage.trim()) p.set('maxage', maxage.trim());
      // Der Endpunkt antwortet mit `groups`, nicht `results` — hier stand
      // erst `results`, und die Suche meldete deshalb bei jedem Treffer
      // "Keine Treffer", obwohl 185 Gruppen zurueckkamen.
      const d = await api<{ groups?: Gruppe[]; total_groups?: number; total_releases?: number }>(
        `/search/grouped?${p}`,
      );
      setGruppen(d.groups ?? []);
      setGesamt({ gruppen: d.total_groups ?? 0, releases: d.total_releases ?? 0 });
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
      setGruppen(null);
      setGesamt(null);
    } finally {
      setLaedt(false);
    }
  }

  // Filter laufen im Browser: Die Antwort ist schon da, ein zweiter
  // Netz-Umweg fuer eine Auswahl waere nur langsamer.
  const gefiltert = useMemo(() => {
    if (!gruppen) return null;
    const min = minGB.trim() ? Number(minGB) * 1024 ** 3 : 0;
    return gruppen
      .map((g) => {
        const rel = (g.releases ?? []).filter((r) =>
          (res.length === 0 || res.includes(r.tags?.resolution)) &&
          (!min || (r.size ?? 0) >= min));
        return { ...g, releases: rel };
      })
      .filter((g) => g.releases.length > 0);
  }, [gruppen, res, minGB]);

  return (
    <>
      <div className="kopf">
        <div>
          <h1>Suche</h1>
          <p>Einmalig etwas holen — oder aus einem Treffer direkt einen Auftrag machen.</p>
        </div>
      </div>

      <form className="karte" style={{ padding: 14, marginBottom: 16 }} onSubmit={suchen}>
        <div className="reihe" style={{ gap: 10, flexWrap: 'wrap' }}>
          <input className="feld" style={{ flex: '2 1 280px' }} value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Film oder Serie …" autoFocus />
          <input className="feld" style={{ flex: '0 1 190px' }} value={maxage} inputMode="numeric"
            onChange={(e) => setMaxage(e.target.value)}
            placeholder="höchstens … Tage alt" />
          <button className="knopf knopf--haupt" type="submit" disabled={laedt || !q.trim()}>
            <Lupe size={16} /> {laedt ? 'Sucht …' : 'Suchen'}
          </button>
        </div>

        <div className="reihe" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
          <span className="klein schwach">Auflösung:</span>
          {AUFLOESUNGEN.map((r) => (
            <button key={r} type="button"
              className={`chip ${res.includes(r) ? 'chip--akzent' : 'chip--neutral'}`}
              style={{ cursor: 'pointer', border: 'none' }}
              onClick={() => setRes((a) => a.includes(r) ? a.filter((x) => x !== r) : [...a, r])}>
              {r}
            </button>
          ))}
          <span className="klein schwach" style={{ marginLeft: 10 }}>ab</span>
          <input className="feld" style={{ width: 92 }} value={minGB} inputMode="decimal"
            onChange={(e) => setMinGB(e.target.value)} placeholder="GB" />
          {(res.length > 0 || minGB) && (
            <button className="knopf knopf--klein" type="button"
              onClick={() => { setRes([]); setMinGB(''); }}>zurücksetzen</button>
          )}
        </div>
      </form>

      {fehler && <div className="hinweis hinweis--schlecht">{fehler}</div>}

      {!gefiltert ? (
        <div className="karte karte__leer">
          Suchbegriff eingeben. Filter für Auflösung und Größe wirken sofort auf das Ergebnis.
        </div>
      ) : gefiltert.length === 0 ? (
        <div className="karte karte__leer">
          {gruppen?.length ? 'Nichts passt zu den Filtern.' : 'Keine Treffer.'}
        </div>
      ) : (
        <div className="stapel">
          {gesamt && (
            <div className="klein schwach">
              {gefiltert.length === gesamt.gruppen
                ? `${gesamt.gruppen} Treffer mit ${gesamt.releases} Fassungen`
                : `${gefiltert.length} von ${gesamt.gruppen} Treffern nach Filter`}
            </div>
          )}
          {gefiltert.map((g) => (
            <Treffer key={g.key} g={g} aufAuftrag={() => setJobAus(g)} />
          ))}
        </div>
      )}

      {jobAus && (
        <JobDialog
          job={{
            id: 0, user_id: 0,
            name: `${jobAus.title}${jobAus.year ? ` (${jobAus.year})` : ''}`,
            search_query: jobAus.title,
            search_type: jobAus.type === 'tv' ? 'tv' : jobAus.type === 'movie' ? 'movie' : 'general',
            category: null, cron_expression: '0 */6 * * *',
            is_enabled: true, auto_download: true,
            min_size_mb: null, max_size_mb: null, regex_pattern: null,
            preferred_resolution: res[0] ?? jobAus.resolutions[0] ?? null,
            target_season: jobAus.seasons.length ? Math.max(...jobAus.seasons) : null,
            extra_params: null, upgrade_propers: true, quality_cutoff: null,
            last_run_at: null, created_at: '', updated_at: '',
          }}
          // id 0 heisst "noch nicht gespeichert" — der Dialog legt neu an.
          neuTrotzVorlage
          aufSchliessen={() => setJobAus(null)}
          aufFertig={() => setJobAus(null)}
        />
      )}
    </>
  );
}

function Treffer({ g, aufAuftrag }: { g: Gruppe; aufAuftrag: () => void }) {
  const [alle, setAlle] = useState(false);
  const [laedt, setLaedt] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const sichtbar = alle ? g.releases : g.releases.slice(0, 5);

  async function holen(r: Release) {
    setLaedt(r.guid);
    setMeldung(null);
    try {
      await api('/downloads', {
        method: 'POST',
        body: JSON.stringify({ guid: r.guid, title: r.title, size_bytes: r.size, category: null }),
      });
      setMeldung(`„${r.title.slice(0, 48)}…" an NZBGet übergeben.`);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaedt(null);
    }
  }

  return (
    <div className="karte">
      <div className="karte__kopf" style={{ alignItems: 'flex-start', gap: 14 }}>
        {g.cover && (
          <img src={g.cover} alt="" width={54} height={80} loading="lazy"
            style={{ borderRadius: 6, objectFit: 'cover', flex: '0 0 auto' }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="reihe" style={{ flexWrap: 'wrap', gap: 7 }}>
            <span className="karte__titel" style={{ fontSize: 15 }}>{g.title}</span>
            {g.year && <span className="leise klein">{g.year}</span>}
            {g.rating != null && (
              <span className="chip chip--neutral"><Star size={11} /> {Number(g.rating).toFixed(1)}</span>
            )}
            <span className="chip chip--neutral">{g.type === 'tv' ? 'Serie' : g.type === 'movie' ? 'Film' : 'sonstiges'}</span>
          </div>
          <div className="klein leise" style={{ marginTop: 5 }}>
            {g.releases.length} {g.releases.length === 1 ? 'Fassung' : 'Fassungen'}
            {g.resolutions.length > 0 && <> · {g.resolutions.join(', ')}</>}
            {g.seasons.length > 0 && <> · Staffeln {[...g.seasons].sort((a, b) => a - b).join(', ')}</>}
          </div>
          {g.plot && <div className="klein schwach" style={{ marginTop: 6, maxWidth: 680 }}>{g.plot.slice(0, 190)}{g.plot.length > 190 ? '…' : ''}</div>}
        </div>
        <button className="knopf knopf--klein" onClick={aufAuftrag} style={{ flex: '0 0 auto' }}>
          <Clock size={14} /> Automatik anlegen
        </button>
      </div>

      {meldung && <div className="dialog__leib" style={{ paddingBottom: 0 }}><div className="hinweis klein">{meldung}</div></div>}

      <table className="tabelle">
        <tbody>
          {sichtbar.map((r) => (
            <tr key={r.guid}>
              <td className="titel-zelle">
                {r.recommended && <span className="chip chip--gut" style={{ marginRight: 7 }}>Empfehlung</span>}
                {r.title}
              </td>
              <td style={{ width: 90 }}>
                {r.tags?.resolution && <span className="chip chip--neutral">{r.tags.resolution}</span>}
              </td>
              <td className="num klein leise" style={{ width: 80 }}>{bytes(r.size)}</td>
              <td className="num klein schwach" style={{ width: 90 }}>{relativ(r.posted_at)}</td>
              <td style={{ width: 110 }}>
                <button className="knopf knopf--klein" disabled={laedt === r.guid} onClick={() => holen(r)}>
                  <DlIcon size={14} /> {laedt === r.guid ? '…' : 'Laden'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {g.releases.length > 5 && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--rand)' }}>
          <button className="knopf knopf--klein" onClick={() => setAlle(!alle)}>
            {alle ? 'weniger zeigen' : `alle ${g.releases.length} Fassungen zeigen`}
          </button>
        </div>
      )}
    </div>
  );
}
